import { useRef, useCallback } from 'react';
import { v4 as uuid } from 'uuid';

import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';

import { IMAGE } from 'hb-react/shared/components/DesignSystem/QuillEditor/constants';
import { useQuillEditorContext } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';
import readAsDataURL from 'hb-react/shared/utils/readAsDataURL';

import InputFileHidden from '../components/InputFileHidden';
import ToolbarItem from '../components/ToolbarItem';
import getFileInputClickHandler from '../utils/getFileInputClickHandler';

const ACCEPT_IMAGES = ['image/gif', 'image/png', 'image/jpeg'];

const InlineImage = () => {
  const { onAddFiles, options, quillRef, setForceEditorFocus } =
    useQuillEditorContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAddInlineImage = useCallback(
    async (files: Array<File> = []) => {
      if (!quillRef.current || !onAddFiles) {
        return;
      }

      quillRef.current?.focus();

      const keys = files.map(() => uuid());

      onAddFiles?.({
        type: IMAGE,
        files,
        keys,
      });

      await Promise.all(
        files.filter(Boolean).map(async (file, index) => {
          const base64ImageSrc = await readAsDataURL(file);

          const key = keys[index];

          const range = quillRef.current?.getSelection();
          const lastIndex = quillRef.current?.getLength() ?? 0;
          const insertAt = range?.index ?? lastIndex - 1;

          quillRef.current?.insertEmbed(
            insertAt,
            'inline-image',
            {
              src: base64ImageSrc,
              id: key,
            },
            'user',
          );
        }),
      );

      setForceEditorFocus?.(false);
    },
    [onAddFiles, quillRef, setForceEditorFocus],
  );

  const { toolbar: toolbarOptions, tooltips: tooltipsOptions } = options ?? {};

  return (
    <>
      <ToolbarItem
        onMouseDown={() => setForceEditorFocus?.(true)}
        onClick={getFileInputClickHandler(inputRef)}
        icon={<FontAwesomeIcon iconName="image" height="16px" />}
        disabled={
          typeof toolbarOptions?.inlineImage === 'object'
            ? toolbarOptions?.inlineImage?.disabled
            : false
        }
        dataTestId="inline-image-action"
        ariaLabel={s__('QuillEditorAccessibility|Add an image')}
        tooltip={tooltipsOptions?.inlineImage}
        enableFontAwesomeIcon
      />
      <InputFileHidden
        ref={inputRef}
        dataTestId="inline-image-action-input"
        accept={ACCEPT_IMAGES}
        onChange={handleAddInlineImage}
      />
    </>
  );
};

export default InlineImage;
